<script setup>
import { computed, ref, watch } from "vue"
import debounce from "lodash.debounce"
import { useStore } from "vuex"
import { apiUrl, defaultImage } from "@common/js/helpers"
import Loading from "@common/js/components/loaders/ListLoader.vue"

const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value()
      }
    }
    document.addEventListener("click", el.clickOutsideEvent)
  },
  unmounted(el) {
    document.removeEventListener("click", el.clickOutsideEvent)
  },
}

const store = useStore()
const query = ref("")
const showProductList = ref(false)
const loading = ref(false)
const items = ref([])
watch(query, (newValue) => {
  if (newValue.length > 0) {
    showProductList.value = true
  }
})

const debounceFn = debounce(async (value) => {
  try {
    loading.value = true
    const result = await store.dispatch("products/fetchAll", {
      categories: ["HARDWARE"],
      title: value ? value : null,
    })
    items.value = result.items
  } catch (e) {
    console.log("e: ", e)
  } finally {
    loading.value = false
  }
}, 500)

const onHandleFocusOut = () => {
  showProductList.value = false
}
const onHandleFocus = () => {
  if (query.value.length > 0) {
    showProductList.value = true
  }
}
</script>

<template>
  <div
    id="searchbar-desktop"
    v-click-outside="() => onHandleFocusOut()"
    class="relative"
  >
    <div>
      <div>
        <div class="relative">
          <input
            v-model="query"
            type="search"
            :placeholder="
              $t('common.shop.hardware.navigation.search-bar.placeholder')
            "
            class="rounded-full border-none bg-gray-200 py-3 pl-5 pr-12 text-sm text-gray-600 placeholder:text-gray-400 focus-visible:ring-0 w-full"
            @input="(event) => debounceFn(event.target.value)"
            @focus="onHandleFocus"
          /><button
            class="absolute left-auto right-[2px] top-1/2 flex -translate-y-1/2 items-center rounded-full bg-white p-3"
            type="submit"
            aria-label="Search"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10.9585 12.3036C9.82175 13.1076 8.42637 13.5812 6.91828 13.5812C3.09742 13.5812 0 10.541 0 6.79062C0 3.04027 3.09742 0 6.91828 0C10.7391 0 13.8366 3.04027 13.8366 6.79062C13.8366 8.42187 13.2506 9.91877 12.2739 11.0896L16.0005 14.7474L14.7244 16L10.9585 12.3036ZM1.80556 6.79086C1.80556 9.56286 4.09495 11.81 6.91907 11.81C9.74318 11.81 12.0326 9.56286 12.0326 6.79086C12.0326 4.01885 9.74318 1.7717 6.91907 1.7717C4.09495 1.7717 1.80556 4.01885 1.80556 6.79086Z"
                fill="black"
              ></path>
            </svg>
          </button>
        </div>
        <div
          v-if="query.length > 0 && showProductList"
          class="absolute inset-x-6 top-full z-50 max-h-[65vh] overflow-auto rounded-b-xl border bg-white shadow-lg"
        >
          <Loading v-if="loading" />
          <div
            v-else-if="items.length > 0"
            class="p-2 md:p-4 col-span-full relative"
          >
            <h4 class="mb-2 px-2 font-bold text-neutral">
              {{ $t("common.shop.hardware.search-bar.results") }}
            </h4>
            <ul>
              <li
                v-for="(item, index) in items"
                :key="index"
                class="bullet-none block"
              >
                <div :index-name="`products_${index}`">
                  <router-link
                    class="flex rounded px-4 py-2 text-base font-semibold no-underline hover:bg-gray-200 hover:no-underline"
                    :to="{
                      name: 'HardwareMarketplaceShopProduct',
                      params: { slug: item.slug },
                    }"
                    @click="
                      () => {
                        query = ''
                        showProductList = false
                      }
                    "
                    ><img
                      class="mr-4 aspect-square w-16 h-16 object-contain object-center"
                      :src="
                        item.thumbnailImage
                          ? apiUrl(item.thumbnailImage.thumbnail.url)
                          : defaultImage
                      "
                      :alt="item.title"
                    />
                    <div>
                      <div>
                        <span>{{ item.title }}</span>
                      </div>
                      <div
                        v-if="item.sku"
                        class="text-sm font-thin text-gray-500"
                      >
                        <span>{{ item.sku }}</span>
                      </div>
                    </div></router-link
                  >
                </div>
              </li>
            </ul>
          </div>
          <div
            class="flex w-full px-4 py-2"
            :class="items.length > 0 ? 'bg-gray-200' : 'bg-white'"
          >
            <svg
              data-v-60641edd=""
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              class="mr-2 h-4 w-4"
              width="20"
            >
              <path
                d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z"
              ></path>
              <path
                d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z"
              ></path>
            </svg>
            {{ $t("common.shop.hardware.search-bar.search-for") }}
            <strong class="ml-1 group-hover:underline pr-1"
              >{{ query }}
            </strong>
            <span v-if="items.length === 0">
              - {{ $t("common.shop.hardware.search-bar.no-results") }}
            </span>
          </div>
        </div>
        <!---->
      </div>
    </div>
  </div>
</template>
